import { batch, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { stringify } from 'qs';

import { TSegmentedBannerAlias } from '@r1-frontend/api-domru/spec-offer/v1/banners/dto/responseDto';
import { EOfferState } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/dto/ISpecOffer';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { useConnectSpeedBonus } from '~/src/features/specialOffersContainer/hooks/useConnectSpeedBonus';
import { setIsPopupOpened, setPopupState } from '~/src/features/specialOffersContainer/store/actions';
import { updateOffer } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { selectIsOfferTransactionPayed } from '~/src/features/specialOffersContainer/store/selectors';
import { TPromoCallbackMap } from '~/src/features/specialOffersContainer/types';

// Типы оборудования по категориям, передаются при создании договора в поле 'equip_product_id'
// @todo collect all product ids in the project and place it in one place
export const equipTypes: Record<string, number> = {
    'routers': 5,
    'router': 5, // This case happens at the additional products of tariff.
    'private-sector-equipment': 5,
    'decoder': 53, // This case didn't happen, but anything can happen.
    'decoders': 53,
    'yandex-stanciya': 5,
};

export const useCallbackMap = (root?: TSegmentedBannerAlias): TPromoCallbackMap<typeof DefaultCallback> => {
    const router = useRouter();
    const dispatch = useDispatch();
    const connectSpeedBonus = useConnectSpeedBonus();
    const isOfferTransactionPayed = useSelector(selectIsOfferTransactionPayed);

    return {
        onActivate: () => ({
            callback: async() => {
                dispatch(updateOffer());
            },
        }),
        onPay: ({ paySum, state }) => ({
            on: () => !isOfferTransactionPayed && state !== EOfferState.CONNECTED && (paySum || 0) > 0,
            callback: async() => {
                batch(() => {
                    dispatch(setIsPopupOpened(false));
                    dispatch(setPopupState('needPayment'));
                });
            },
        }),
        onSetPhone: offer => ({
            on: () => offer.billingState !== 'connected' && (offer.benefits?.some(({ needPhone }) => needPhone) ?? false),
            callback: async() => {
                specialOfferAnalytics.connectWithPhone(offer);
                batch(() => {
                    dispatch(setIsPopupOpened(false));
                    dispatch(setPopupState('needSetPhone'));
                });
            },
        }),
        onEquipment: ({ navigation, requestId }) => ({
            on: () => navigation?.type === 'equipment',
            loader: 'local',
            callback: async() => {
                if (!navigation?.pricesV2) {
                    return;
                }

                const {
                    id: ensId = 0,
                    pricesV2: { duration = 0 },
                    category,
                } = navigation;

                const productId = equipTypes[category]; // TODO: Fix later, remove equipTypes, need to provide productId from backend and get from navigation object directly

                router
                    .push(
                        `/full-buy-equip?${stringify({
                            ensId,
                            productId,
                            requestId,
                            duration,
                            category,
                        })}`,
                    )
                    .then(() => {
                        dispatch(setIsPopupOpened(false));
                    });
            },
        }),
        onMultiSubcription: ({ navigation }) => ({
            on: () => navigation?.type === 'multisubscription',
            loader: 'local',
            callback: () => router
                .push('/lk/multibundle')
                .then(() => {
                    dispatch(setIsPopupOpened(false));
                }),
        }),
        onChangeTariff: ({ navigation }) => ({
            on: () => navigation?.type === 'promoPacket',
            loader: 'local',
            callback: () => router
                .push(
                    `/lk/change-tariff?${stringify({
                        promoid: navigation?.id,
                        mode: 'details',
                        referrer: `/${root}`,
                    })}`,
                )
                .then(() => {
                    dispatch(setIsPopupOpened(false));
                }),
        }),
        onPendingRequest: ({ navigation }) => ({
            on: () => navigation?.type === 'pendingConnection',
            loader: 'local',
            callback: () => router
                .push('/lk/orders')
                .then(() => {
                    dispatch(setIsPopupOpened(false));
                }),
        }),
        onSpeedBonusConnect: ({ navigation, state }) => ({
            on: () => navigation?.type === 'speedBonusWithRequest' && !!navigation?.shopItemId && state !== EOfferState.CONNECTED,
            loader: 'local',
            callback: () => connectSpeedBonus(navigation),
        }),
    };
};
